'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, AtSign, StickyNote, Loader2, Trash2,  } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Comment {
  id: string;
  work_order_id: string;
  author_id: string;
  author_name: string;
  author_avatar: string;
  content: string;
  mentions: string[];
  is_note: boolean;
  created_at: string;
}

interface TeamMember {
  id: string;
  name: string;
  avatar: string;
  role: string;
}

interface WorkOrderCommentsProps {
  workOrderId: string;
  currentUser: { id: string; name: string; avatar: string; role: string };
}

// ─── Team members for @mention ────────────────────────────────────────────────

const TEAM_MEMBERS: TeamMember[] = [
  { id: 'tm-1', name: 'Dawit Taddese', avatar: 'DT', role: 'Network Engineer' },
  { id: 'tm-2', name: 'Selam Bekele', avatar: 'SB', role: 'NOC Analyst' },
  { id: 'tm-3', name: 'Yonas Girma', avatar: 'YG', role: 'Network Engineer' },
  { id: 'tm-4', name: 'Hiwot Alemu', avatar: 'HA', role: 'NOC Analyst' },
  { id: 'tm-5', name: 'Biruk Haile', avatar: 'BH', role: 'Field Technician' },
  { id: 'tm-6', name: 'Tigist Worku', avatar: 'TW', role: 'Team Lead' },
  { id: 'tm-7', name: 'Abel Mekonnen', avatar: 'AM', role: 'Network Engineer' },
];

const AVATAR_COLORS: Record<string, string> = {
  DT: 'bg-blue-500/20 text-blue-400',
  SB: 'bg-green-500/20 text-green-400',
  YG: 'bg-purple-500/20 text-purple-400',
  HA: 'bg-pink-500/20 text-pink-400',
  BH: 'bg-orange-500/20 text-orange-400',
  TW: 'bg-cyan-500/20 text-cyan-400',
  AM: 'bg-yellow-500/20 text-yellow-400',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function renderContent(content: string): React.ReactNode {
  const parts = content.split(/(@[\w\s]+(?=\s|$|[^a-zA-Z\s]))/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} className="text-primary font-semibold bg-primary/10 px-0.5 rounded">
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function MiniAvatar({ initials }: { initials: string }) {
  return (
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
        AVATAR_COLORS[initials] || 'bg-primary/20 text-primary'
      }`}
    >
      {initials}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WorkOrderComments({ workOrderId, currentUser }: WorkOrderCommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [input, setInput] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);
  const [tab, setTab] = useState<'all' | 'notes' | 'comments'>('all');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  // ─── Load comments ──────────────────────────────────────────────────────────

  const loadComments = useCallback(async () => {
    const { data, error } = await supabase
      .from('work_order_comments')
      .select('*')
      .eq('work_order_id', workOrderId)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setComments(data as Comment[]);
    }
    setLoading(false);
  }, [workOrderId, supabase]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // ─── Real-time subscription ─────────────────────────────────────────────────

  useEffect(() => {
    const channel = supabase
      .channel(`wo-comments-${workOrderId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'work_order_comments',
          filter: `work_order_id=eq.${workOrderId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setComments((prev) => {
              const exists = prev.some((c) => c.id === (payload.new as Comment).id);
              if (exists) return prev;
              return [...prev, payload.new as Comment];
            });
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          } else if (payload.eventType === 'DELETE') {
            setComments((prev) => prev.filter((c) => c.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workOrderId, supabase]);

  // ─── @mention detection ─────────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? 0;
    setInput(val);
    setCursorPos(pos);

    // Detect @mention trigger
    const textBefore = val.slice(0, pos);
    const mentionMatch = textBefore.match(/@([\w ]*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const filteredMembers = mentionQuery !== null
    ? TEAM_MEMBERS.filter(
        (m) =>
          m.name.toLowerCase().includes(mentionQuery.toLowerCase()) &&
          m.id !== currentUser.id
      )
    : [];

  const insertMention = (member: TeamMember) => {
    const textBefore = input.slice(0, cursorPos);
    const textAfter = input.slice(cursorPos);
    const mentionStart = textBefore.lastIndexOf('@');
    const newText = textBefore.slice(0, mentionStart) + `@${member.name} ` + textAfter;
    setInput(newText);
    setMentionQuery(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => Math.min(i + 1, filteredMembers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMembers[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed || submitting) return;

    // Extract mentions from content
    const mentionMatches = trimmed.match(/@([\w ]+)/g) || [];
    const mentions = mentionMatches
      .map((m) => m.slice(1).trim())
      .filter((name) => TEAM_MEMBERS.some((tm) => tm.name === name));

    setSubmitting(true);
    const { error } = await supabase.from('work_order_comments').insert({
      work_order_id: workOrderId,
      author_id: currentUser.id,
      author_name: currentUser.name,
      author_avatar: currentUser.avatar,
      content: trimmed,
      mentions,
      is_note: isNote,
    });

    if (!error) {
      setInput('');
      setIsNote(false);
      setMentionQuery(null);
    }
    setSubmitting(false);
  };

  const handleDelete = async (id: string) => {
    await supabase.from('work_order_comments').delete().eq('id', id);
  };

  // ─── Filtered display ───────────────────────────────────────────────────────

  const displayed = comments.filter((c) => {
    if (tab === 'notes') return c.is_note;
    if (tab === 'comments') return !c.is_note;
    return true;
  });

  const noteCount = comments.filter((c) => c.is_note).length;
  const commentCount = comments.filter((c) => !c.is_note).length;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Header tabs */}
      <div className="flex items-center gap-1 px-4 pt-3 pb-2 border-b border-border flex-shrink-0">
        <MessageSquare size={13} className="text-primary mr-1" />
        <span className="text-xs font-semibold text-foreground mr-3">Team Collaboration</span>
        <div className="flex items-center gap-0.5 ml-auto">
          {(['all', 'comments', 'notes'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                tab === t
                  ? 'bg-primary/15 text-primary' :'text-muted hover:text-foreground hover:bg-surface-raised'
              }`}
            >
              {t === 'all' ? `All (${comments.length})` : t === 'comments' ? `Comments (${commentCount})` : `Notes (${noteCount})`}
            </button>
          ))}
        </div>
      </div>

      {/* Comment list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={18} className="text-muted animate-spin" />
          </div>
        ) : displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <MessageSquare size={24} className="text-muted opacity-30 mb-2" />
            <p className="text-xs text-muted">No {tab === 'all' ? 'messages' : tab} yet</p>
            <p className="text-[11px] text-muted opacity-60 mt-0.5">Be the first to add one</p>
          </div>
        ) : (
          displayed.map((comment) => (
            <div
              key={comment.id}
              className={`group flex items-start gap-2.5 ${
                comment.is_note ? 'bg-yellow-400/5 border border-yellow-400/15 rounded-lg p-2.5' : ''
              }`}
            >
              <MiniAvatar initials={comment.author_avatar} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-foreground">{comment.author_name}</span>
                  {comment.is_note && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded">
                      <StickyNote size={9} /> Note
                    </span>
                  )}
                  {comment.mentions.length > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                      <AtSign size={9} /> {comment.mentions.length}
                    </span>
                  )}
                  <span className="text-[10px] text-muted font-mono ml-auto">{formatTime(comment.created_at)}</span>
                  {comment.author_id === currentUser.id && (
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="opacity-0 group-hover:opacity-100 text-muted hover:text-danger transition-all"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
                <p className="text-xs text-foreground-muted leading-relaxed">
                  {renderContent(comment.content)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 px-4 pb-4 pt-2 border-t border-border relative">
        {/* @mention dropdown */}
        {mentionQuery !== null && filteredMembers.length > 0 && (
          <div className="absolute bottom-full left-4 right-4 mb-1 bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-20">
            <div className="px-3 py-1.5 border-b border-border">
              <span className="text-[10px] text-muted font-medium uppercase tracking-wider">Mention a team member</span>
            </div>
            {filteredMembers.map((member, idx) => (
              <button
                key={member.id}
                onClick={() => insertMention(member)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  idx === mentionIndex ? 'bg-primary/10' : 'hover:bg-surface-raised'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    AVATAR_COLORS[member.avatar] || 'bg-primary/20 text-primary'
                  }`}
                >
                  {member.avatar}
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">{member.name}</p>
                  <p className="text-[10px] text-muted">{member.role}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Toggle note/comment */}
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setIsNote(false)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
              !isNote ? 'bg-primary/15 text-primary' : 'text-muted hover:text-foreground hover:bg-surface-raised'
            }`}
          >
            <MessageSquare size={11} /> Comment
          </button>
          <button
            onClick={() => setIsNote(true)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
              isNote ? 'bg-yellow-400/15 text-yellow-400' : 'text-muted hover:text-foreground hover:bg-surface-raised'
            }`}
          >
            <StickyNote size={11} /> Note
          </button>
          <span className="text-[10px] text-muted ml-auto flex items-center gap-1">
            <AtSign size={10} /> to mention · ⌘↵ to send
          </span>
        </div>

        <div className="flex items-end gap-2">
          <MiniAvatar initials={currentUser.avatar} />
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isNote ? 'Add a private note...' : 'Write a comment, use @ to mention...'}
              rows={2}
              className={`w-full bg-surface-raised border rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted focus:outline-none resize-none transition-colors ${
                isNote
                  ? 'border-yellow-400/30 focus:border-yellow-400/60' :'border-border focus:border-primary/50'
              }`}
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || submitting}
            className="flex items-center justify-center w-8 h-8 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
}
